import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiUrl } from '../utils/apiBaseUrl';

const Register: React.FC = () => {
  const [searchParams] = useSearchParams();
  const invitationToken = searchParams.get('token');
  const isInvitation = Boolean(searchParams.get('invite') === 'true' && invitationToken);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
    org_name: '',
    billing_email: '',
  });
  const [accountType, setAccountType] = useState<'business' | 'insurer'>('business');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidatingToken, setIsValidatingToken] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [invitationKind, setInvitationKind] = useState<'client' | 'insurer_team'>('client');
  const [inviteErrorCode, setInviteErrorCode] = useState<string | null>(null);
  const [inviteOrgName, setInviteOrgName] = useState<string | null>(null);

  const navigate = useNavigate();
  const { register, login } = useAuth();

  // Validate invitation token and pre-fill email
  useEffect(() => {
    if (isInvitation && invitationToken) {
      validateInvitationToken(invitationToken);
    }
  }, [isInvitation, invitationToken]);

  const validateInvitationToken = async (token: string) => {
    setIsValidatingToken(true);
    setInviteErrorCode(null);
    try {
      const response = await fetch(apiUrl(`/v1/auth/validate-invitation/${token}`));
      const data = await response.json();
      if (data.valid) {
        setFormData(prev => ({ ...prev, email: data.email }));
        setInviteOrgName(data.org_name || null);
        if (data.invitation_kind === 'insurer_team') {
          setInvitationKind('insurer_team');
        }
      } else {
        setInviteErrorCode(data.error_code || 'invalid');
        setInviteOrgName(data.org_name || null);
        if (data.email) {
          setFormData(prev => ({ ...prev, email: data.email }));
        }
        setError(data.message || 'Invalid invitation link');
      }
    } catch (err: any) {
      setError('Failed to validate invitation. Please try again.');
    } finally {
      setIsValidatingToken(false);
    }
  };

  const handleRequestInviteResend = async () => {
    if (!invitationToken) return;
    try {
      await fetch(apiUrl('/v1/auth/request-invite-resend'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: invitationToken }),
      });
      setError('Your organization has been notified to resend your invitation.');
    } catch {
      setError('Could not notify your organization. Contact them directly.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Calculate password strength
    if (name === 'password') {
      let strength = 0;
      if (value.length >= 8) strength += 25;
      if (/[A-Z]/.test(value)) strength += 25;
      if (/[a-z]/.test(value)) strength += 25;
      if (/[0-9]/.test(value)) strength += 25;
      setPasswordStrength(strength);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);

    try {
      if (isInvitation && invitationToken) {
        // Call invitation-based registration
        const response = await fetch(apiUrl('/v1/auth/register-with-invitation'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: invitationToken,
            password: formData.password,
            full_name: formData.full_name || undefined
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Registration failed');
        }

        await response.json();

        const inviteEmail = formData.email;
        const invitePassword = formData.password;
        try {
          await login(inviteEmail, invitePassword);
        } catch {
          setError('Your account was created. Sign in with the password you just set.');
          navigate('/login', { replace: true, state: { email: inviteEmail } });
          return;
        }

        if (invitationKind === 'client') {
          navigate('/tabletop-exercises', { replace: true });
        } else if (invitationKind === 'insurer_team') {
          navigate('/insurer-dashboard', { replace: true });
        } else {
          navigate('/tabletop-exercises', { replace: true });
        }
        return;
      } else if (accountType === 'insurer') {
        const response = await fetch(apiUrl('/v1/auth/register-insurer'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            org_name: formData.org_name,
            billing_email: formData.billing_email || formData.email,
            email: formData.email,
            password: formData.password,
            full_name: formData.full_name || undefined,
          }),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Registration failed');
        }
        await response.json();
      } else {
        await register({
          email: formData.email,
          password: formData.password,
          full_name: formData.full_name || undefined
        });
      }

      // Show success message and redirect to login
      setError('');
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        full_name: '',
        org_name: '',
        billing_email: '',
      });

      if (accountType === 'insurer') {
        alert('Application submitted. We will review your organization and email you when approved.');
        navigate('/login');
      } else {
        alert('Registration successful! Please check your email to verify your account before logging in.');
      }
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength < 50) return 'bg-red-500';
    if (passwordStrength < 75) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength < 25) return 'Weak';
    if (passwordStrength < 50) return 'Fair';
    if (passwordStrength < 75) return 'Good';
    return 'Strong';
  };

  return (
    <div className="flex flex-col justify-center py-12 min-h-screen bg-gray-50 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Logo */}
        <div className="flex justify-center">
          <img
            src="/flashailogo.png"
            alt="Flash AI"
            className="h-16 w-auto"
            onError={(e) => {
              // Fallback if logo doesn't exist
              (e.target as HTMLImageElement).style.display = 'none';
            }}
          />
        </div>
        <h2 className="mt-6 text-3xl font-extrabold text-center text-gray-900">
          {isInvitation
            ? invitationKind === 'insurer_team'
              ? 'Join your team on Flash AI™'
              : 'Complete Your Registration'
            : 'Create your account'}
        </h2>
        <p className="mt-2 text-sm text-center text-gray-600">
          {isInvitation
            ? invitationKind === 'insurer_team'
              ? `Set your password to join ${inviteOrgName || 'your team'} on Flash AI™.`
              : inviteOrgName
                ? `${inviteOrgName} invited you to Flash AI™. Set your password to get started.`
                : 'You\'ve been invited! Set your password to complete your account setup.'
            : 'Join Flash AI™ and secure your business continuity'}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="px-4 py-8 bg-white shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="p-4 bg-red-50 rounded-md border border-red-200">
                <div className="flex">
                  <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="ml-3">
                    <p className="text-sm text-red-800">{error}</p>
                    {inviteErrorCode === 'expired' && (
                      <button
                        type="button"
                        onClick={handleRequestInviteResend}
                        className="mt-2 text-sm font-semibold text-blue-700 hover:text-blue-900"
                      >
                        Notify {inviteOrgName || 'your organization'} to resend invite
                      </button>
                    )}
                    {inviteErrorCode === 'already_used' && (
                      <Link to="/login" className="mt-2 inline-block text-sm font-semibold text-blue-700 hover:text-blue-900">
                        Log in instead
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            )}

            {!isInvitation && (
              <div>
                <span className="block text-sm font-medium text-gray-700 mb-2">Account type</span>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setAccountType('business')}
                    className={`px-3 py-2 rounded-md border text-sm font-medium ${
                      accountType === 'business'
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Business
                  </button>
                  <button
                    type="button"
                    onClick={() => setAccountType('insurer')}
                    className={`px-3 py-2 rounded-md border text-sm font-medium ${
                      accountType === 'insurer'
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Insurance partner
                  </button>
                </div>
              </div>
            )}

            {!isInvitation && accountType === 'insurer' && (
              <>
                <div>
                  <label htmlFor="org_name" className="block text-sm font-medium text-gray-700">
                    Organization name
                  </label>
                  <input
                    id="org_name"
                    name="org_name"
                    type="text"
                    required
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md sm:text-sm"
                    value={formData.org_name}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label htmlFor="billing_email" className="block text-sm font-medium text-gray-700">
                    Billing email
                  </label>
                  <input
                    id="billing_email"
                    name="billing_email"
                    type="email"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md sm:text-sm"
                    placeholder="Defaults to your login email"
                    value={formData.billing_email}
                    onChange={handleChange}
                  />
                </div>
              </>
            )}

            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-gray-700">
                Full Name <span className="text-gray-400">(Optional)</span>
              </label>
              <div className="relative mt-1">
                <input
                  id="full_name"
                  name="full_name"
                  type="text"
                  autoComplete="name"
                  className="block px-3 py-2 pl-10 w-full placeholder-gray-400 rounded-md border border-gray-300 appearance-none focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your full name"
                  value={formData.full_name}
                  onChange={handleChange}
                />
                <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address <span className="text-red-500">*</span>
              </label>
              <div className="relative mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  disabled={isInvitation || isValidatingToken}
                  className={`block px-3 py-2 pl-10 w-full placeholder-gray-400 rounded-md border border-gray-300 appearance-none focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${isInvitation ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder={isValidatingToken ? 'Validating invitation...' : 'Enter your email'}
                  value={formData.email}
                  onChange={handleChange}
                />
                <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative mt-1">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  className="block px-3 py-2 pr-10 pl-10 w-full placeholder-gray-400 rounded-md border border-gray-300 appearance-none focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Create a password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <button
                  type="button"
                  className="flex absolute inset-y-0 right-0 items-center pr-3"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5 text-gray-400 hover:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-gray-400 hover:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>

              {/* Password Strength Indicator */}
              {formData.password && (
                <div className="mt-2">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs text-gray-600">Password strength</span>
                    <span className={`text-xs font-medium ${passwordStrength < 50 ? 'text-red-600' : passwordStrength < 75 ? 'text-yellow-600' : 'text-green-600'}`}>
                      {getPasswordStrengthText()}
                    </span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                      style={{ width: `${passwordStrength}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <div className="relative mt-1">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  required
                  className="block px-3 py-2 pr-10 pl-10 w-full placeholder-gray-400 rounded-md border border-gray-300 appearance-none focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <button
                  type="button"
                  className="flex absolute inset-y-0 right-0 items-center pr-3"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <svg className="w-5 h-5 text-gray-400 hover:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-gray-400 hover:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className="flex items-center mt-1 text-sm text-red-600">
                  <svg className="mr-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Passwords do not match
                </p>
              )}
            </div>

            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="terms"
                  name="terms"
                  type="checkbox"
                  required
                  className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="terms" className="text-gray-700">
                  I agree to the{' '}
                  <Link to="/terms-of-service" className="font-medium text-blue-600 hover:text-blue-500">
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy-policy" className="font-medium text-blue-600 hover:text-blue-500">
                    Privacy Policy
                  </Link>
                </label>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="flex relative justify-center px-4 py-2 w-full text-sm font-medium text-white bg-blue-600 rounded-md border border-transparent group hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isLoading && (
                  <svg className="mr-3 -ml-1 w-5 h-5 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isLoading ? 'Creating account...' : 'Create account'}
              </button>
            </div>

            {/* Features Preview */}
            <div className="p-4 bg-blue-50 rounded-md border border-blue-200">
              <h4 className="mb-2 text-sm font-medium text-blue-800">What you'll get:</h4>
              <ul className="space-y-1 text-xs text-blue-700">
                {isInvitation && invitationKind === 'client' ? (
                  <>
                    <li className="flex items-center">
                      <svg className="mr-2 w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      AI-generated tabletop exercise scenarios
                    </li>
                    <li className="flex items-center">
                      <svg className="mr-2 w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Standalone exercises — no BCP required
                    </li>
                    <li className="flex items-center">
                      <svg className="mr-2 w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Credit provisioned by {inviteOrgName || 'your insurance partner'}
                    </li>
                  </>
                ) : isInvitation && invitationKind === 'insurer_team' ? (
                  <>
                    <li className="flex items-center">
                      <svg className="mr-2 w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Client invitations and tabletop credit management
                    </li>
                    <li className="flex items-center">
                      <svg className="mr-2 w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Team dashboard for {inviteOrgName || 'your organization'}
                    </li>
                  </>
                ) : (
                  <>
                    <li className="flex items-center">
                      <svg className="mr-2 w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      AI-powered BCP generation
                    </li>
                    <li className="flex items-center">
                      <svg className="mr-2 w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      ISO 22301 compliance
                    </li>
                    <li className="flex items-center">
                      <svg className="mr-2 w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Real-time risk assessment
                    </li>
                    <li className="flex items-center">
                      <svg className="mr-2 w-3 h-3 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Crisis communication tools
                    </li>
                  </>
                )}
              </ul>
            </div>
          </form>

          <div className="mt-6">
            <div className="text-center">
              <span className="text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
                  Sign in here
                </Link>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;